import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import React from 'react';

import type { CurrencyDynamoItem } from '../../../../../services/document-settings/DocumentSettingsTypes.ts';
import useNewDocumentSettings from '../DocumentDesignSettings/useNewDocumentSettings.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { defaultCurrencySettings, selectActiveCurrencySettings } from '../../../grid/reduxStore/documentSettingsSlice.ts';
import SidePanelAutoComplete from '../../component/SidePanelAutoComplete.tsx';
import EllipsisMenuItem from './EllipsisMenuItem.tsx';

export type CurrencyOptionProp = {
  code: string;
  label: string;
  symbol: string;
  isDivider?: boolean;
};

// mock data for currency options;
const defaultCurrencyOptions = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'GBP', symbol: '£', name: 'Pound Sterling' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', isDivider: true },
];
const defaultCurrencyCodes = defaultCurrencyOptions.map((c) => c.code);

interface CurrencyAutocompleteProps {
  autocompleteTitle: string;
  currencies?: CurrencyDynamoItem[];
}

const CurrencyAutocomplete = ({ autocompleteTitle, currencies }: CurrencyAutocompleteProps) => {
  const { t } = useTranslation();
  const filteredCurrencies = (currencies ?? []).filter((c) => c.code && !defaultCurrencyCodes.includes(c.code));
  const { setDocumentSettings, documentSettings } = useNewDocumentSettings();
  const activeCurrency = useAppSelector(selectActiveCurrencySettings) || defaultCurrencySettings;
  const activeCurrencyEntity = (currencies ?? [])?.find((c) => c.code === activeCurrency.code);
  const createCurrencyLabel = (option) => {
    return `${option.name} (${option.code})`;
  };

  const activeOption = { ...activeCurrency, label: createCurrencyLabel(activeCurrencyEntity) };
  const options = [...defaultCurrencyOptions, ...filteredCurrencies].map(
    (option): CurrencyOptionProp => ({
      code: option.code || '',
      symbol: option.symbol || '',
      label: createCurrencyLabel(option),
      isDivider: (option as CurrencyOptionProp).isDivider || false,
    })
  );

  const onUpdateDocumentCurrency = async (event: CurrencyOptionProp) => {
    const { code, symbol } = event;
    setDocumentSettings({ ...documentSettings, currencySettings: { code, symbol } });
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="currency-options-container">
      <Typography sx={{ marginBottom: 1.5, marginTop: 2, fontSize: '14px' }} color="primary" align="left" variant="subtitle2">
        {t('document.currency.title')}
      </Typography>
      <SidePanelAutoComplete<CurrencyOptionProp>
        disableClearable
        data-testid="document-settings-currency-autocomplete"
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            padding: '1px 65px 1px 12px',
          },
        }}
        slotProps={{
          popper: {
            placement: 'bottom-start',
            sx: {
              '& .MuiAutocomplete-listbox': {
                maxHeight: '200px',
              },
            },
          },
        }}
        options={options}
        testId="currency-select"
        value={activeOption}
        hideSearchIcon
        label={autocompleteTitle}
        renderOptions={(props, options) => (
          <React.Fragment key={`currency-${options.code.toLowerCase()}`}>
            <MenuItem {...props} key={options.code} value={options.code}>
              <EllipsisMenuItem text={options.label} />
            </MenuItem>
            {options.isDivider && <Divider />}
          </React.Fragment>
        )}
        onChange={(event) => event && onUpdateDocumentCurrency(event)}
        noOptionsText={t('document.currency.no_currency')}
        textDataTestId="currency-select-text-input"
        isOptionEqualToValue={(option: CurrencyOptionProp, value) => option.code === value.code}
      />
    </Box>
  );
};

export default CurrencyAutocomplete;
